import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getAlerts, createAlert, acknowledgeAlert, resolveAlert, deleteAlert, getDevices, assignAlert, getGroups, getUsers } from "../api/axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Form, FormControl,  FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { queryClient } from "../lib/queryClient";
import { format } from "date-fns";
import { 
  Bell, 
  AlertTriangle, 
  AlertOctagon, 
  AlertCircle, 
  Info, 
  CheckCircle, 
  Clock, 
  Filter, 
  Plus, 
  RotateCw,
  RefreshCw,
  Activity,
  Settings,
  ExternalLink,
  UserPlus,
  Users,
  UserCog
} from "lucide-react";

// Form schema
const alertFormSchema = z.object({
  deviceId: z.string().min(1, { message: "Please select a device" }),
  title: z.string().min(1, { message: "Title is required" }),
  message: z.string().min(1, { message: "Message is required" }),
  severity: z.string().min(1, { message: "Please select a severity level" }),
  type: z.string().min(1, { message: "Please select an alert type" }),
  threshold: z.number().optional().nullable(),
});

type AlertFormValues = z.infer<typeof alertFormSchema>;

type Group = {
  _id: string;
  name: string;
  members?: string[] | any[];
};

type User = {
  _id: string;
  username: string;
  displayname: string;
  email: string;
  role: string;
};

export default function Alerts() {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<any>(null);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [extraEmails, setExtraEmails] = useState<string>("");
  const [notifyOnAssign, setNotifyOnAssign] = useState(true);
  const [filter, setFilter] = useState({
    severity: "all",
    status: "all",
  });

  // Form setup
  const form = useForm<AlertFormValues>({
    resolver: zodResolver(alertFormSchema),
    defaultValues: {
      deviceId: "",
      title: "",
      message: "",
      severity: "warning",
      type: "",
      threshold: null,
    },
  });

  // Fetch alerts
  const { data: alerts = [], isLoading: isLoadingAlerts, refetch: refetchAlerts } = useQuery<any[]>({
    queryKey: ["/api/alerts", filter.severity, filter.status],
    queryFn: () => {
      const params: { severity?: string; acknowledged?: boolean; resolved?: boolean } = {};
      if (filter.severity !== "all") params.severity = filter.severity;
      if (filter.status === "open") { params.acknowledged = false; params.resolved = false; }
      if (filter.status === "acknowledged") { params.acknowledged = true; params.resolved = false; }
      if (filter.status === "resolved") { params.resolved = true; }
      return getAlerts(params);
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0, // Always consider data stale to ensure fresh data after mutations
  });

  // Fetch devices for the dropdown
  const { data: devices = [] } = useQuery({
    queryKey: ["/api/devices"],
    queryFn: getDevices,
  });

  // Fetch groups for assignment and display (always fetch to show group names)
  const { data: groups = [] } = useQuery<Group[]>({
    queryKey: ["groups"],
    queryFn: () => getGroups({ withMembers: true }),
    staleTime: 5 * 60 * 1000,
    enabled: currentUser?.role === "admin" || currentUser?.role === "superadmin",
  });

  // Fetch users for assignment and display (always fetch to show user names)
  const { data: users = [] } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn: getUsers,
    staleTime: 5 * 60 * 1000,
    enabled: currentUser?.role === "admin" || currentUser?.role === "superadmin",
  });

  // Create alert mutation
  const createAlertMutation = useMutation({
    mutationFn: async (data: AlertFormValues) => {
      return createAlert({
        deviceId: data.deviceId,
        severity: data.severity,
        message: data.message,
        details: data.title,
        threshold: data.threshold || undefined
      });
    },
    onSuccess: () => {
      toast({
        title: "Alert created",
        description: "The alert has been created successfully",
      });
      setIsCreateDialogOpen(false);
      form.reset();
      queryClient.invalidateQueries({ queryKey: ["/api/alerts"] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Acknowledge alert mutation
  const acknowledgeAlertMutation = useMutation({
    mutationFn: async (alertId: string) => {
      return acknowledgeAlert(alertId);
    },
    onSuccess: () => {
      toast({
        title: "Alert acknowledged",
        description: "The alert has been acknowledged",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/alerts"] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Resolve alert mutation
  const resolveAlertMutation = useMutation({
    mutationFn: async (alertId: string) => {
      return resolveAlert(alertId);
    },
    onSuccess: () => {
      toast({
        title: "Alert resolved",
        description: "The alert has been resolved",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/alerts"] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete alert mutation
  const deleteAlertMutation = useMutation({
    mutationFn: async (alertId: string) => {
      return deleteAlert(alertId);
    },
    onSuccess: () => {
      toast({
        title: "Alert deleted",
        description: "The alert has been permanently removed",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/alerts"] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Assign alert mutation
  const assignAlertMutation = useMutation({
    mutationFn: async ({ alertId, assignmentData }: { alertId: string; assignmentData: { groupIds?: string[]; userIds?: string[]; extraEmails?: string[]; notify?: boolean } }) => {
      return assignAlert(alertId, assignmentData);
    },
    onSuccess: async () => {
      // Close dialog and reset form state first
      setIsAssignDialogOpen(false);
      setSelectedAlert(null);
      setSelectedGroupIds([]);
      setSelectedUserIds([]);
      setExtraEmails("");
      setNotifyOnAssign(true);
      
      // Show success toast immediately
      toast({
        title: "Success",
        description: "Alert has been assigned successfully",
        variant: "default",
      });
      
      // Force refetch all active alert queries immediately
      // Using refetchQueries bypasses staleTime and forces a fresh fetch
      await queryClient.refetchQueries({ 
        queryKey: ["/api/alerts"],
        exact: false,
        type: 'active' // Only refetch active queries
      });
      
      // Also refetch related alert queries
      await queryClient.refetchQueries({ 
        queryKey: ["/api/live-alerts"],
        exact: false,
        type: 'active'
      });
      
      await queryClient.refetchQueries({ 
        queryKey: ["/api/alerts/recent"],
        exact: false,
        type: 'active'
      });
    },
    onError: (error: any) => {
      // Extract detailed error message from validation error
      let errorMessage = "Failed to assign alert";
      
      // Log full error details for debugging
      const errorData = error.response?.data;
      console.error("Assign alert error details:", {
        response: errorData,
        status: error.response?.status,
        validationErrors: errorData?.data,
        firstValidationError: errorData?.data?.[0],
        fullError: error
      });
      
      // Also log the validation error array items individually
      if (errorData?.data && Array.isArray(errorData.data)) {
        console.error("Validation errors breakdown:", errorData.data.map((err: any, idx: number) => ({
          index: idx,
          field: err.field,
          message: err.message,
          type: err.type,
          value: err.value,
          expected: err.expected,
          fullError: err
        })));
        
        // Log the first error in detail for immediate visibility
        const firstError = errorData.data[0];
        if (firstError) {
          console.error("FIRST VALIDATION ERROR:", JSON.stringify(firstError, null, 2));
          console.error("Error field:", firstError.field);
          console.error("Error message:", firstError.message);
          console.error("Error type:", firstError.type);
          console.error("Error value:", firstError.value);
        }
      }
      
      if (errorData) {
        // Moleculer validation errors have a 'data' array with field-specific errors
        if (errorData.data && Array.isArray(errorData.data) && errorData.data.length > 0) {
          // Validation errors array - format each error with full details
          const validationErrors = errorData.data.map((err: any) => {
            const fieldName = err.field === 'id' ? 'Alert ID' : 
                             err.field === 'groupIds' ? 'Groups' :
                             err.field === 'userIds' ? 'Users' :
                             err.field === 'extraEmails' ? 'Email addresses' :
                             err.field === 'notify' ? 'Notification setting' :
                             err.field || 'Unknown field';
            
            // Extract all possible error details
            const details = [];
            if (err.message) details.push(err.message);
            if (err.type) details.push(`Type: ${err.type}`);
            if (err.value !== undefined) details.push(`Value: ${JSON.stringify(err.value)}`);
            if (err.expected) details.push(`Expected: ${err.expected}`);
            
            const errorDetail = details.length > 0 ? details.join(' - ') : 'Invalid value';
            return `${fieldName}: ${errorDetail}`;
          });
          errorMessage = `Validation Error:\n${validationErrors.join('\n')}`;
        } else if (errorData.message) {
          errorMessage = errorData.message;
          // If there's additional data, append it
          if (errorData.data && Array.isArray(errorData.data) && errorData.data.length > 0) {
            errorMessage += `\nDetails: ${JSON.stringify(errorData.data, null, 2)}`;
          }
        } else if (errorData.type) {
          errorMessage = `Validation error: ${errorData.type}`;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else {
          // Try to extract any error information
          errorMessage = `Validation failed: ${JSON.stringify(errorData, null, 2)}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Close dialog on error too
      setIsAssignDialogOpen(false);
      setSelectedAlert(null);
      setSelectedGroupIds([]);
      setSelectedUserIds([]);
      setExtraEmails("");
      setNotifyOnAssign(true);
      
      toast({
        title: "Failed",
        description: errorMessage,
        variant: "destructive",
        duration: 5000,
      });
    },
  });

  const onSubmit = (data: AlertFormValues) => {
    createAlertMutation.mutate(data);
  };

  // Handle opening assign dialog
  const handleOpenAssignDialog = (alert: any) => {
    setSelectedAlert(alert);
    // Pre-populate with existing assignments if any
    const groupIds = alert.assignedGroupIds?.map((id: any) => {
      if (typeof id === 'object') return (id._id || id.id)?.toString() || '';
      return id?.toString() || '';
    }).filter(Boolean) || [];
    const userIds = alert.assignedUserIds?.map((id: any) => {
      if (typeof id === 'object') return (id._id || id.id)?.toString() || '';
      return id?.toString() || '';
    }).filter(Boolean) || [];
    setSelectedGroupIds(groupIds);
    setSelectedUserIds(userIds);
    setExtraEmails(""); // Reset extra emails when opening dialog
    setIsAssignDialogOpen(true);
  };

  // Handle assigning alert
  const handleAssignAlert = () => {
    if (!selectedAlert) return;
    
    // Parse extra emails from comma-separated string
    const extraEmailsArray = extraEmails
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0 && email.includes('@'));

    if (selectedGroupIds.length === 0 && selectedUserIds.length === 0 && extraEmailsArray.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one group, user, or enter email addresses to assign",
        variant: "destructive",
      });
      return;
    }

    // Build assignment data - only include arrays if they have values (backend expects optional arrays)
    const assignmentData: { groupIds?: string[]; userIds?: string[]; extraEmails?: string[]; notify: boolean } = {
      notify: notifyOnAssign,
    };

    // Only include arrays if they have values
    if (selectedGroupIds.length > 0) {
      assignmentData.groupIds = selectedGroupIds;
    }
    if (selectedUserIds.length > 0) {
      assignmentData.userIds = selectedUserIds;
    }
    if (extraEmailsArray.length > 0) {
      assignmentData.extraEmails = extraEmailsArray;
    }

    // Trigger the mutation - dialog will close in onSuccess/onError
    assignAlertMutation.mutate({
      alertId: selectedAlert.id || selectedAlert._id,
      assignmentData,
    });
  };

  // Toggle group selection
  const toggleGroupSelection = (groupId: string) => {
    setSelectedGroupIds(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  // Toggle user selection
  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Get group name by id
  const getGroupName = (groupId: string) => {
    const group = groups.find((g: Group) => g._id?.toString() === groupId?.toString());
    return group ? group.name : `Group ${groupId}`;
  };

  // Get user name by id
  const getUserName = (userId: string) => {
    const user = users.find((u: User) => u._id?.toString() === userId?.toString());
    return user ? user.displayname || user.username : `User ${userId}`;
  };

  // Alert severity badge
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case "critical":
        return <Badge variant="destructive" className="flex items-center gap-1"><AlertOctagon className="h-3 w-3" /> Critical</Badge>;
      case "warning":
        return <Badge className="bg-orange-500 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Warning</Badge>;
      case "info":
        return <Badge variant="outline" className="flex items-center gap-1"><Info className="h-3 w-3" /> Info</Badge>;
      default:
        return <Badge variant="outline" className="flex items-center gap-1"><Info className="h-3 w-3" /> {severity}</Badge>;
    }
  };

  // Alert status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "resolved":
        return <Badge variant="outline" className="bg-green-100 text-green-800 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Resolved</Badge>;
      case "acknowledged":
        return <Badge variant="outline" className="bg-blue-100 text-blue-800 flex items-center gap-1"><Clock className="h-3 w-3" /> Acknowledged</Badge>;
      case "open":
        return <Badge variant="outline" className="bg-red-100 text-red-800 flex items-center gap-1"><Bell className="h-3 w-3" /> Open</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Find device name by id
  const getDeviceName = (deviceId: any) => {
    if (!deviceId) return 'Unknown Device';
    // Handle deviceId as string, ObjectId, or object with _id
    const deviceIdStr = typeof deviceId === 'object' 
      ? (deviceId._id?.toString() || deviceId.toString()) 
      : deviceId.toString();
    const device = devices.find((d: any) => {
      const dId = (d._id || d.id)?.toString();
      return dId === deviceIdStr;
    });
    return device ? device.name : `Device ${deviceIdStr}`;
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    try {
      return format(new Date(timestamp), "PPp");
    } catch (e) {
      return "Invalid date";
    }
  };

  // Map alerts to consistent format with status derived from acknowledged/resolved fields
  const formattedAlerts = alerts.map((alert: any) => {
    let status = 'open';
    if (alert.resolved) status = 'resolved';
    else if (alert.acknowledged) status = 'acknowledged';
    
    // Handle deviceId - can be ObjectId string or ObjectId object
    const deviceId = alert.deviceId?._id || alert.deviceId?.toString() || alert.deviceId;
    
    return {
      ...alert,
      id: alert._id || alert.id,
      deviceId: deviceId, // Normalize deviceId to string
      status,
      title: alert.details || alert.type || 'Alert', // Use details, type, or default
    };
  });
  
  // Filter alerts
  const filteredAlerts = formattedAlerts.filter((alert: any) => {
    if (filter.severity && filter.severity !== 'all' && alert.severity !== filter.severity) return false;
    if (filter.status && filter.status !== 'all' && alert.status !== filter.status) return false;
    return true;
  });

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Settings className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold">Alert Management</h1>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Link to="/live-alerts">
            <Button variant="outline" size="sm" className="flex items-center gap-1">
              <Activity className="h-4 w-4 text-red-500" />
              View Live Alerts
              <ExternalLink className="h-3 w-3" />
            </Button>
          </Link>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => refetchAlerts()}
            className="flex items-center gap-1"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="flex items-center gap-1">
                <Plus className="h-4 w-4" />
                Create Alert
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
              <DialogHeader>
                <DialogTitle>Create New Alert</DialogTitle>
                <DialogDescription>
                  Create a new alert to notify about an issue or event.
                </DialogDescription>
              </DialogHeader>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 max-h-[calc(80vh-180px)] overflow-y-auto p-2 pr-2">
                  <FormField
                    control={form.control}
                    name="deviceId"
                    render={({ field }) => (
                      <FormItem className="mb-1">
                        <FormLabel className="text-sm">Device</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="h-8">
                              <SelectValue placeholder="Select a device" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {devices.map((device: any) => (
                              <SelectItem key={(device._id || device.id).toString()} value={(device._id || device.id).toString()}>
                                {device.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem className="mb-1">
                        <FormLabel className="text-sm">Title</FormLabel>
                        <FormControl>
                          <Input className="h-8" placeholder="Alert title" {...field} />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem className="mb-1">
                        <FormLabel className="text-sm">Message</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Describe the alert..." 
                            className="min-h-[60px] resize-none"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name="severity"
                      render={({ field }) => (
                        <FormItem className="mb-1">
                          <FormLabel className="text-sm">Severity</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Select severity" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="info">Info</SelectItem>
                              <SelectItem value="warning">Warning</SelectItem>
                              <SelectItem value="critical">Critical</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem className="mb-1">
                          <FormLabel className="text-sm">Alert Type</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="cpu_usage">CPU Usage</SelectItem>
                              <SelectItem value="memory_usage">Memory Usage</SelectItem>
                              <SelectItem value="disk_usage">Disk Usage</SelectItem>
                              <SelectItem value="network_traffic">Network Traffic</SelectItem>
                              <SelectItem value="response_time">Response Time</SelectItem>
                              <SelectItem value="error_rate">Error Rate</SelectItem>
                              <SelectItem value="custom">Custom</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="threshold"
                    render={({ field }) => (
                      <FormItem className="mb-1">
                        <FormLabel className="text-sm">Threshold</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            className="h-8"
                            placeholder="Enter threshold value" 
                            {...field}
                            onChange={(e) => {
                              const value = e.target.value ? parseInt(e.target.value) : null;
                              field.onChange(value);
                            }}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  <DialogFooter className="pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setIsCreateDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit"
                      disabled={createAlertMutation.isPending}
                    >
                      {createAlertMutation.isPending && (
                        <RotateCw className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Create Alert
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Alerts</p>
                <p className="text-2xl font-bold">{alerts.length}</p>
              </div>
              <Bell className="h-6 w-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open</p>
                <p className="text-2xl font-bold text-red-600">
                  {formattedAlerts.filter(a => a.status === 'open').length}
                </p>
              </div>
              <AlertOctagon className="h-6 w-6 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Acknowledged</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formattedAlerts.filter(a => a.status === 'acknowledged').length}
                </p>
              </div>
              <Clock className="h-6 w-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Resolved</p>
                <p className="text-2xl font-bold text-green-600">
                  {formattedAlerts.filter(a => a.status === 'resolved').length}
                </p>
              </div>
              <CheckCircle className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center justify-center">
            <Link to="/live-alerts" className="w-full">
              <Button variant="outline" size="sm" className="w-full flex items-center gap-2">
                <Activity className="h-4 w-4 text-red-500" />
                View Live Alerts
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-blue-600" />
                Alert Management & History
              </CardTitle>
              <CardDescription>
                Create new alerts, manage existing ones, and view alert history across all statuses
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select 
                  value={filter.severity} 
                  onValueChange={(value) => setFilter((prev) => ({ ...prev, severity: value }))}
                >
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="All Severities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severities</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Select 
                  value={filter.status} 
                  onValueChange={(value) => setFilter((prev) => ({ ...prev, status: value }))}
                >
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="acknowledged">Acknowledged</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingAlerts ? (
            <div className="flex justify-center items-center py-8">
              <RotateCw className="h-8 w-8 animate-spin text-primary/70" />
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No alerts found</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {(filter.severity !== "all" || filter.status !== "all")
                  ? "Try changing your filters or create a new alert" 
                  : "You don't have any alerts. Create one to get started"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAlerts.map((alert: any) => (
                    <TableRow key={alert.id}>
                      <TableCell className="whitespace-nowrap">
                        {formatTimestamp(alert.timestamp)}
                      </TableCell>
                      <TableCell>
                        {getDeviceName(alert.deviceId)}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{alert.title}</div>
                          <div className="text-sm text-muted-foreground">
                            {alert.message.length > 50 
                              ? `${alert.message.substring(0, 50)}...` 
                              : alert.message}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getSeverityBadge(alert.severity)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(alert.status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {alert.assignedGroupIds && alert.assignedGroupIds.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {alert.assignedGroupIds.map((groupId: any) => {
                                const id = typeof groupId === 'object' ? (groupId._id || groupId.id) : groupId;
                                const idStr = id?.toString() || '';
                                return (
                                  <Badge key={idStr} variant="secondary" className="text-xs">
                                    <UserCog className="h-3 w-3 mr-1" />
                                    {idStr ? getGroupName(idStr) : 'Group'}
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                          {alert.assignedUserIds && alert.assignedUserIds.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {alert.assignedUserIds.map((userId: any) => {
                                const id = typeof userId === 'object' ? (userId._id || userId.id) : userId;
                                const idStr = id?.toString() || '';
                                const userName = idStr ? getUserName(idStr) : 'User';
                                return (
                                  <Badge key={idStr} variant="outline" className="text-xs">
                                    <Users className="h-3 w-3 mr-1" />
                                    {userName}
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                          {alert.extraEmails && alert.extraEmails.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {alert.extraEmails.map((email: string, idx: number) => (
                                <Badge key={`email-${idx}`} variant="secondary" className="text-xs">
                                  <Info className="h-3 w-3 mr-1" />
                                  {email}
                                </Badge>
                              ))}
                            </div>
                          )}
                          {(!alert.assignedGroupIds || alert.assignedGroupIds.length === 0) &&
                           (!alert.assignedUserIds || alert.assignedUserIds.length === 0) &&
                           (!alert.extraEmails || alert.extraEmails.length === 0) && (
                            <span className="text-xs text-muted-foreground">Unassigned</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {(currentUser?.role === "admin" || currentUser?.role === "superadmin") && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleOpenAssignDialog(alert)}
                              disabled={assignAlertMutation.isPending}
                            >
                              <UserPlus className="h-3 w-3 mr-1" />
                              Assign
                            </Button>
                          )}
                          {alert.status === "open" && (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => acknowledgeAlertMutation.mutate(alert.id)}
                                disabled={acknowledgeAlertMutation.isPending}
                              >
                                {acknowledgeAlertMutation.isPending && alert.id === acknowledgeAlertMutation.variables && (
                                  <RotateCw className="mr-1 h-3 w-3 animate-spin" />
                                )}
                                Acknowledge
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => resolveAlertMutation.mutate(alert.id)}
                                disabled={resolveAlertMutation.isPending}
                              >
                                {resolveAlertMutation.isPending && alert.id === resolveAlertMutation.variables && (
                                  <RotateCw className="mr-1 h-3 w-3 animate-spin" />
                                )}
                                Resolve
                              </Button>
                            </>
                          )}
                          {alert.status === "acknowledged" && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => resolveAlertMutation.mutate(alert.id)}
                              disabled={resolveAlertMutation.isPending}
                            >
                              {resolveAlertMutation.isPending && alert.id === resolveAlertMutation.variables && (
                                <RotateCw className="mr-1 h-3 w-3 animate-spin" />
                              )}
                              Resolve
                            </Button>
                          )}
                          {alert.status === "resolved" && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => deleteAlertMutation.mutate(alert.id)}
                              disabled={deleteAlertMutation.isPending}
                            >
                              {deleteAlertMutation.isPending && alert.id === deleteAlertMutation.variables && (
                                <RotateCw className="mr-1 h-3 w-3 animate-spin" />
                              )}
                              Delete
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Alert Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Alert</DialogTitle>
            <DialogDescription>
              Assign this alert to groups and/or users. They will be notified via email if enabled.
            </DialogDescription>
          </DialogHeader>

          {selectedAlert && (
            <div className="space-y-4 py-4">
              {/* Alert Info */}
              <div className="p-3 bg-gray-50 rounded-md">
                <div className="text-sm font-medium">{selectedAlert.title || "Alert"}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {getDeviceName(selectedAlert.deviceId)} • {getSeverityBadge(selectedAlert.severity)}
                </div>
              </div>

              {/* Groups Selection */}
              <div className="space-y-2">
                <Label>Assign to Groups</Label>
                <div className="border rounded-md p-4 max-h-48 overflow-y-auto">
                  {groups.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No groups available</p>
                  ) : (
                    <div className="space-y-2">
                      {groups.map((group: Group) => {
                        const groupId = group._id?.toString() || "";
                        if (!groupId) return null;
                        return (
                          <div key={groupId} className="flex items-center space-x-2">
                            <Checkbox
                              id={`group-${groupId}`}
                              checked={selectedGroupIds.includes(groupId)}
                              onCheckedChange={() => toggleGroupSelection(groupId)}
                            />
                            <Label
                              htmlFor={`group-${groupId}`}
                              className="text-sm font-normal cursor-pointer flex-1"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <UserCog className="h-4 w-4 text-gray-500" />
                                  <span>{group.name}</span>
                                </div>
                                {group.members && Array.isArray(group.members) && (
                                  <span className="text-xs text-muted-foreground">
                                    {group.members.length} member{group.members.length !== 1 ? "s" : ""}
                                  </span>
                                )}
                              </div>
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Users Selection */}
              <div className="space-y-2">
                <Label>Assign to Users</Label>
                <div className="border rounded-md p-4 max-h-48 overflow-y-auto">
                  {users.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No users available</p>
                  ) : (
                    <div className="space-y-2">
                      {users.map((user: User) => {
                        const userId = user._id?.toString() || "";
                        if (!userId) return null;
                        return (
                          <div key={userId} className="flex items-center space-x-2">
                            <Checkbox
                              id={`user-${userId}`}
                              checked={selectedUserIds.includes(userId)}
                              onCheckedChange={() => toggleUserSelection(userId)}
                            />
                            <Label
                              htmlFor={`user-${userId}`}
                              className="text-sm font-normal cursor-pointer flex-1"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-medium">{user.displayname || user.username}</div>
                                  <div className="text-xs text-muted-foreground">
                                    @{user.username} • {user.email}
                                  </div>
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {user.role}
                                </Badge>
                              </div>
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Extra Emails */}
              <div className="space-y-2">
                <Label htmlFor="extraEmails">Additional Email Addresses (Optional)</Label>
                <Input
                  id="extraEmails"
                  type="text"
                  placeholder="email1@example.com, email2@example.com"
                  value={extraEmails}
                  onChange={(e) => setExtraEmails(e.target.value)}
                  className="h-8"
                />
                <p className="text-xs text-muted-foreground">
                  Enter comma-separated email addresses to notify additional recipients
                </p>
              </div>

              {/* Notify Option */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div className="space-y-0.5">
                  <Label htmlFor="notify" className="text-sm font-medium">
                    Send Email Notification
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Notify assigned groups/users via email
                  </p>
                </div>
                <Switch
                  id="notify"
                  checked={notifyOnAssign}
                  onCheckedChange={setNotifyOnAssign}
                />
              </div>

              {/* Selection Summary */}
              {(selectedGroupIds.length > 0 || selectedUserIds.length > 0) && (
                <div className="p-3 bg-blue-50 rounded-md">
                  <p className="text-sm font-medium text-blue-900 mb-2">Selected:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedGroupIds.map((groupId) => (
                      <Badge key={groupId} variant="secondary" className="text-xs">
                        <UserCog className="h-3 w-3 mr-1" />
                        {getGroupName(groupId)}
                      </Badge>
                    ))}
                    {selectedUserIds.map((userId) => (
                      <Badge key={userId} variant="outline" className="text-xs">
                        <Users className="h-3 w-3 mr-1" />
                        {getUserName(userId)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsAssignDialogOpen(false);
                setSelectedAlert(null);
                setSelectedGroupIds([]);
                setSelectedUserIds([]);
                setExtraEmails("");
                setNotifyOnAssign(true);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignAlert}
              disabled={assignAlertMutation.isPending}
            >
              {assignAlertMutation.isPending ? (
                <>
                  <RotateCw className="mr-2 h-4 w-4 animate-spin" />
                  Assigning...
                </>
              ) : (
                "Assign Alert"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}